import type { StartingBuilding } from './economy';
import { FACTIONS } from './factions';
import type { BuildingId, FactionId, GoodId, ShipClassId } from './ids';
import { TRADE_GOOD_IDS, type TradeGoodId } from './market';

/**
 * Starting holdings. Every player begins with the same money and a small
 * stock of everything, plus a surplus of the good their faction makes
 * cheaply — the first thing worth selling (GDD §4, comparative advantage).
 */
export const STARTING_CASH = 1000;
const BASE_UNITS = 10;
const SPECIALTY_UNITS = 60;

export function startingGoods(faction: FactionId): Record<TradeGoodId, number> {
  const goods = Object.fromEntries(
    TRADE_GOOD_IDS.map((id) => [id, id === 'exotics' ? 0 : BASE_UNITS]),
  ) as Record<TradeGoodId, number>;
  goods[FACTIONS[faction].cheapGood] = SPECIALTY_UNITS;
  return goods;
}

/** The building that makes each faction's cheap good on its home world. */
const SPECIALTY_BUILDING: Readonly<Record<GoodId, BuildingId>> = {
  food: 'agroComplex',
  helium3: 'miningComplex',
  alloys: 'smelter',
  electronics: 'electronicsFactory',
};

/**
 * A capital starts with housing, a power plant and the producer of the
 * faction's cheap good on the surface (one of four slots stays free) and a
 * spaceport in orbit.
 */
export function startingBuildings(faction: FactionId): readonly StartingBuilding[] {
  return [
    { id: SPECIALTY_BUILDING[FACTIONS[faction].cheapGood], layer: 'surface' },
    { id: 'powerPlant', layer: 'surface' },
    { id: 'housing', layer: 'surface' },
    { id: 'spaceport', layer: 'orbit' },
  ];
}

/**
 * Ships every capital starts with, as one fleet: an explorer, so dust cloud
 * scouting (GDD §11: the first 15 minutes) never competes with food imports.
 */
export const STARTING_SHIPS: readonly ShipClassId[] = ['explorer'];
